/**
 * WorkDetailPage — fiche œuvre Direction C « Fiche conservateur ».
 *
 * Hero 60/40 : image à gauche, cartel-de-musée à droite (status, auteur,
 * titre italique, métadonnées 2×2, story/description si présentes, toggle
 * FR/EN). Puis 2 onglets existants : Aperçu (corps 2-col : essai éditorial
 * Vision en colonne principale, sidebar métadonnées techniques + tags +
 * intention) et Contenu généré (DraftsTab inchangé).
 *
 * Pipeline FR ↔ EN : `<EditableTranslation>` sur title/description/story
 * permet d'overrider la traduction EN générée par Vision (PATCH /api/works/{id}
 * → bascule `_en_overridden=TRUE` côté BE). Le toggle FR/EN du cartel
 * scope l'affichage. Le panneau Vision est traduit à la volée par Claude
 * en batch (`useTranslateBatch`).
 *
 * `view_count` incrémenté côté BE à chaque GET /works/{id} (transactionnel,
 * verrou check_atomic_counters).
 */

import { useCallback, useMemo, useState } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router'
import {
  AlertTriangle,
  Archive,
  ArrowLeft,
  Copy,
  Image as ImageIcon,
  Pencil,
  RefreshCw,
  Sparkles,
  Trash2,
} from 'lucide-react'
import { toast } from 'sonner'

import AspectRatioLabel from '@/components/ui/AspectRatioLabel'
import Badge from '@/components/ui/Badge'
import Button from '@/components/ui/Button'
import DraftsTab from '@/components/drafts/DraftsTab'
import WorkPublicationsTab from '@/components/work/WorkPublicationsTab'
import EditableTranslation from '@/components/ui/EditableTranslation'
import EmptyState from '@/components/ui/EmptyState'
import FormSection from '@/components/forms/FormSection'
import KebabMenu, { type KebabMenuItem } from '@/components/ui/KebabMenu'
import LangBadge from '@/components/ui/LangBadge'
import MediaGallery from '@/components/ui/MediaGallery'
import MediaImage from '@/components/ui/MediaImage'
import Modal from '@/components/ui/Modal'
import Page from '@/components/layout/Page'
import PortfolioPublishButton from '@/components/works/PortfolioPublishButton'
import WorkRatiosSection from '@/components/works/WorkRatiosSection'
import DeliverablesPanel from '@/components/variants/DeliverablesPanel'
import VariantsPanel from '@/components/variants/VariantsPanel'
import Tabs from '@/components/ui/Tabs'
import {
  useArchiveWork,
  useDeleteWork,
  useDuplicateWork,
  useRetryVision,
  useUpdateWork,
  useVisionJob,
  useWork,
} from '@/hooks/useWorks'
import { useConfirm } from '@/contexts/confirm-dialog-context'
import { useEmotionalIntents, useTargetAudiences } from '@/hooks/useCatalog'
import { useGenerateDrafts, useGenerationBatchPolling } from '@/hooks/useDrafts'
import { useTranslateBatch } from '@/hooks/useTranslate'
import { WORK_ORIENTATION_FR, WORK_STATUS_FR } from '@/types/works'

import './WorkDetailPage.css'

type TabKey = 'overview' | 'content' | 'publications'

const VALID_TABS: ReadonlySet<TabKey> = new Set(['overview', 'content', 'publications'])

function isValidTab(value: string | null): value is TabKey {
  return value !== null && VALID_TABS.has(value as TabKey)
}

interface PaletteEntry {
  name_en?: string
  hex?: string
  emotion_tag?: string
}

export default function WorkDetailPage() {
  const params = useParams<{ id: string }>()
  const id = params.id ? Number(params.id) : null
  const navigate = useNavigate()
  const { confirm } = useConfirm()

  const workQuery = useWork(id)
  const visionJobQuery = useVisionJob(id)
  const updateMutation = useUpdateWork(id ?? 0)
  const archiveMutation = useArchiveWork(id ?? 0)
  const duplicateMutation = useDuplicateWork()
  const deleteMutation = useDeleteWork()
  const retryVisionMutation = useRetryVision(id ?? 0)
  const generateDraftsMutation = useGenerateDrafts(id ?? 0)
  // Tier 2 — slugs paramétrables (S2.5c). On charge tous (actifs+inactifs)
  // pour pouvoir afficher le label_fr d'un slug désactivé que l'œuvre
  // référence encore.
  const emotionalIntentsQuery = useEmotionalIntents()
  const targetAudiencesQuery = useTargetAudiences()

  // Tab state via URL `?tab=publications` (S6 PR4) — permet le deeplink +
  // browser back/forward natifs. Fallback `overview` si pas de query
  // param ou valeur inconnue.
  const [searchParams, setSearchParams] = useSearchParams()
  const tabFromUrl = searchParams.get('tab')
  const tab: TabKey = isValidTab(tabFromUrl) ? tabFromUrl : 'overview'
  const setTab = useCallback(
    (next: TabKey) => {
      setSearchParams(
        (prev) => {
          const params = new URLSearchParams(prev)
          if (next === 'overview') {
            params.delete('tab')
          } else {
            params.set('tab', next)
          }
          return params
        },
        { replace: false },
      )
    },
    [setSearchParams],
  )
  const [lang, setLang] = useState<'fr' | 'en'>('fr')
  const [lightboxKey, setLightboxKey] = useState<string | null>(null)

  // Refactor 2026-05-15 — génération drafts en background (queue S5.5).
  // `activeBatchId` tient le batch_id retourné par l'enqueue ; le polling
  // 3s s'arrête tout seul quand running+scheduled=0 et déclenche
  // l'invalidation drafts via `onSettled`.
  const [activeBatchId, setActiveBatchId] = useState<string | null>(null)
  const batchStatusQuery = useGenerationBatchPolling({
    batchId: activeBatchId,
    workId: id ?? 0,
    onSettled: (status) => {
      setActiveBatchId(null)
      if (status.failed > 0) {
        toast.error(
          `Génération terminée — ${status.done} drafts générés, ${status.failed} en erreur.`,
        )
      } else if (status.done > 0) {
        toast.success(
          `${status.done} draft${status.done > 1 ? 's' : ''} généré${status.done > 1 ? 's' : ''}. À trier sur /drafts.`,
        )
      }
    },
  })
  const isGenerating =
    generateDraftsMutation.isPending ||
    (!!activeBatchId &&
      batchStatusQuery.data != null &&
      batchStatusQuery.data.running + batchStatusQuery.data.scheduled > 0)

  // ── Traduction Vision (à la volée) ────────────────────────────────────────
  // Quand l'opératrice bascule en FR, on traduit le panneau Vision par Claude
  // en batch (visual_description + palette EN + placement_suggestions). Les
  // tags Vision ont déjà leur label_fr, pas de traduction nécessaire.
  const translatePayload = useMemo<{ key: string; text: string }[] | null>(() => {
    const w = workQuery.data
    if (!w || lang !== 'fr') return null
    const paletteEntries: PaletteEntry[] = Array.isArray(w.palette_named)
      ? (w.palette_named as PaletteEntry[])
      : []
    const items: { key: string; text: string }[] = []
    if (w.visual_description) {
      items.push({ key: 'visual_description', text: w.visual_description })
    }
    paletteEntries.forEach((p, i) => {
      if (p.name_en) items.push({ key: `palette:${i}`, text: p.name_en })
    })
    ;(w.placement_suggestions ?? []).forEach((s, i) => {
      items.push({ key: `placement:${i}`, text: s })
    })
    return items.length > 0 ? items : null
  }, [workQuery.data, lang])

  const translateQuery = useTranslateBatch(translatePayload?.map((i) => i.text) ?? null, lang)

  const translatedMap = useMemo(() => {
    const map = new Map<string, string>()
    if (translateQuery.data && translatePayload) {
      translatePayload.forEach((item, i) => {
        const t = translateQuery.data?.translations[i]
        if (t) map.set(item.key, t)
      })
    }
    return map
  }, [translateQuery.data, translatePayload])

  const tFr = (key: string, fallback: string): string => translatedMap.get(key) ?? fallback

  if (id === null || Number.isNaN(id)) {
    return (
      <Page>
        <Page.Header title="Œuvre introuvable" />
        <EmptyState
          title="Identifiant invalide"
          description="L'URL ne contient pas un identifiant d'œuvre valide."
          action={
            <Button variant="primary" onClick={() => navigate('/works')}>
              Retour bibliothèque
            </Button>
          }
        />
      </Page>
    )
  }

  if (workQuery.isLoading) {
    return (
      <Page>
        <p className="ui-text-muted">Chargement…</p>
      </Page>
    )
  }

  if (workQuery.isError || !workQuery.data) {
    return (
      <Page>
        <Page.Header title="Œuvre introuvable" />
        <EmptyState
          title="Œuvre introuvable"
          description={
            (workQuery.error as { detail?: string } | null)?.detail ??
            "L'œuvre n'existe pas ou a été archivée."
          }
          action={
            <Button variant="primary" onClick={() => navigate('/works')}>
              Retour bibliothèque
            </Button>
          }
        />
      </Page>
    )
  }

  const work = workQuery.data
  const visionJob = visionJobQuery.data ?? work.vision_job
  const visionFailed = visionJob?.status === 'failed'
  const visionPending = visionJob?.status === 'pending' || visionJob?.status === 'running'

  // Lookup slug → label_fr depuis le catalogue paramétrable. Fallback : le
  // slug brut, pour ne jamais afficher du vide si Stéphanie a supprimé une
  // entrée encore référencée.
  const emotionalIntentLabel = (slug: string): string =>
    emotionalIntentsQuery.data?.find((r) => r.slug === slug)?.label_fr ?? slug
  const targetAudienceLabel = (slug: string): string =>
    targetAudiencesQuery.data?.find((r) => r.slug === slug)?.label_fr ?? slug

  // Multi-sources par œuvre (migration 015) : le hero affiche le display
  // dont le source parent est `is_canonical=TRUE`. Fallback sur le 1er
  // display puis le 1er thumb si la dérivation canonique n'est pas posée
  // (cas dégradé ou œuvre pré-migration).
  const allMedia = work.media ?? []
  const canonicalSource = allMedia.find((m) => m.kind === 'source' && m.is_canonical) ?? null
  const displayMedia = allMedia.filter((m) => m.kind === 'display')
  const thumbMedia = allMedia.filter((m) => m.kind === 'thumb')

  const canonicalDisplay =
    canonicalSource !== null
      ? (displayMedia.find((m) => m.source_media_id === canonicalSource.id) ?? null)
      : null
  const galleryMedia = displayMedia.length > 0 ? displayMedia : thumbMedia
  const heroMedia = canonicalDisplay ?? galleryMedia[0] ?? null

  // Sources triés par position pour le carrousel (PR 4) ; exposés ici
  // pour propagation à VariantsPanel (matching format ↔ ratio).
  const sourceMedia = allMedia
    .filter((m) => m.kind === 'source')
    .slice()
    .sort((a, b) => (a.position ?? 0) - (b.position ?? 0))
  const sourceRatios = sourceMedia
    .map((m) => {
      const w = m.width
      const h = m.height
      return w && h ? w / h : null
    })
    .filter((r): r is number => r !== null && r > 0)
  const formatMatchingTolerance =
    typeof work.format_matching_tolerance === 'number' ? work.format_matching_tolerance : 0.05 // Fallback transitoire — sera supprimé une fois codegen stabilisé.

  const handleArchive = async () => {
    const confirmed = await confirm({
      title: 'Archiver cette œuvre ?',
      message:
        'Elle ne sera plus visible dans la bibliothèque mais ses données restent en base. Réversible via une mutation manuelle.',
      variant: 'danger',
      confirmText: 'Archiver',
    })
    if (!confirmed) return
    try {
      await archiveMutation.mutateAsync()
      toast.success('Œuvre archivée.')
      navigate('/works')
    } catch (err) {
      const detail = (err as { detail?: string })?.detail ?? 'Erreur inconnue'
      toast.error(`Archivage impossible : ${detail}`)
    }
  }

  const handleDuplicate = async () => {
    if (id === null) return
    try {
      const created = await duplicateMutation.mutateAsync(id)
      toast.success('Copie créée.')
      navigate(`/works/${created.id}/edit`)
    } catch (err) {
      const detail = (err as { detail?: string })?.detail ?? 'Erreur inconnue'
      toast.error(`Duplication impossible : ${detail}`)
    }
  }

  const handleDelete = async () => {
    if (id === null) return
    const confirmed = await confirm({
      title: 'Supprimer cette œuvre ?',
      message:
        'Action irréversible : l’œuvre, ses images, ses tags et ses drafts seront définitivement supprimés.',
      variant: 'danger',
      confirmText: 'Supprimer définitivement',
    })
    if (!confirmed) return
    try {
      await deleteMutation.mutateAsync(id)
      toast.success('Œuvre supprimée.')
      navigate('/works')
    } catch (err) {
      const detail = (err as { detail?: string })?.detail ?? 'Erreur inconnue'
      toast.error(`Suppression impossible : ${detail}`)
    }
  }

  const handleRetryVision = async () => {
    try {
      await retryVisionMutation.mutateAsync()
      toast.success('Extraction Vision relancée.')
    } catch (err) {
      const detail = (err as { detail?: string })?.detail ?? 'Erreur inconnue'
      toast.error(`Relance impossible : ${detail}`)
    }
  }

  const handleGenerateDrafts = async () => {
    try {
      const batch = await generateDraftsMutation.mutateAsync(undefined)
      if (batch.scheduled_count === 0) {
        const reason = batch.skipped[0]?.reason
        if (reason === 'no_missing_kinds') {
          toast.info('Tous les drafts éligibles ont déjà été générés.')
        } else if (reason === 'already_active') {
          toast.info('Une génération est déjà en cours pour cette œuvre.')
        } else {
          toast.info('Aucun draft à générer pour cette œuvre.')
        }
        return
      }
      setActiveBatchId(batch.batch_id)
      toast.info(
        `Génération lancée en arrière-plan — ${batch.scheduled_count} job(s) en file. Les drafts apparaîtront sous 30-60s.`,
      )
    } catch (err) {
      const detail = (err as { detail?: string })?.detail ?? 'Erreur inconnue'
      toast.error(`Génération impossible : ${detail}`)
    }
  }

  const handleSubmitTitleEn = async (newEn: string) => {
    await updateMutation.mutateAsync({ title_en: newEn })
    toast.success('Traduction du titre figée.')
  }

  const handleSubmitDescriptionEn = async (newEn: string) => {
    await updateMutation.mutateAsync({ description_en: newEn })
    toast.success('Traduction de la description figée.')
  }

  const handleSubmitStoryEn = async (newEn: string) => {
    await updateMutation.mutateAsync({ story_en: newEn })
    toast.success('Traduction de la story figée.')
  }

  const palette: PaletteEntry[] = Array.isArray(work.palette_named)
    ? (work.palette_named as PaletteEntry[])
    : []

  // Genres = tags Vision (THEME + SUBJECT, source='vision'). Depuis migration
  // 009, ces axes vivent comme tags avec `work_tags.source='vision'` au lieu
  // de colonnes JSON.
  const visionThemes = (work.tags ?? []).filter((t) => t.kind === 'theme' && t.source === 'vision')
  const visionSubject = (work.tags ?? []).find((t) => t.kind === 'subject' && t.source === 'vision')
  const tagLabel = (t: { label_fr: string | null; label_en?: string | null }): string =>
    lang === 'fr' ? (t.label_fr ?? t.label_en ?? '') : (t.label_en ?? t.label_fr ?? '')

  // User tags (excluding Vision-sourced ones already shown in panel Vision).
  const userTags = (work.tags ?? []).filter(
    (t) =>
      !(t.source === 'vision' && (t.kind === 'theme' || t.kind === 'subject' || t.kind === 'mood')),
  )

  const seriesSubtitle =
    work.series_id !== null && work.series_id !== undefined
      ? `Série · pièce ${work.series_position ?? '—'}`
      : null

  // ── Items du kebab — Modifier la fiche / Dupliquer / Archiver / Supprimer.
  // « Publier sur sgraphic.art » reste un bouton primaire à côté du kebab
  // (cf. <PortfolioPublishButton>), grisé tant que description ≥ 100 chars
  // + story ≥ 80 chars ne sont pas atteints.
  const kebabItems: KebabMenuItem[] = [
    {
      id: 'edit',
      label: 'Modifier la fiche',
      icon: Pencil,
      onSelect: () => navigate(`/works/${work.id}/edit`),
    },
    {
      id: 'duplicate',
      label: 'Dupliquer',
      icon: Copy,
      onSelect: handleDuplicate,
      disabled: duplicateMutation.isPending,
    },
    {
      id: 'archive',
      label: 'Archiver',
      icon: Archive,
      onSelect: handleArchive,
      disabled: work.status === 'archived' || archiveMutation.isPending,
    },
    {
      id: 'retry-vision',
      label: 'Relancer Claude Vision',
      icon: RefreshCw,
      onSelect: handleRetryVision,
      disabled: retryVisionMutation.isPending || visionPending,
    },
    {
      id: 'delete',
      label: 'Supprimer',
      icon: Trash2,
      variant: 'danger',
      onSelect: handleDelete,
      disabled: deleteMutation.isPending,
    },
  ]

  return (
    <Page width="wide">
      <Page.Header
        eyebrow={
          <span className="work-detail-eyebrow">
            <Button variant="quiet" size="sm" type="button" onClick={() => navigate('/works')}>
              <ArrowLeft size={12} aria-hidden="true" /> Bibliothèque
            </Button>
            <span className="work-detail-eyebrow-sep" aria-hidden="true">
              ·
            </span>
            <span className="work-detail-eyebrow-inv">Inv. nº {work.id}</span>
          </span>
        }
        title={work.title_fr}
        meta={
          <div className="work-detail-header-meta">
            {(work.view_count ?? 0) > 0 && (
              <span className="work-detail-stat">
                {work.view_count} vue{(work.view_count ?? 0) > 1 ? 's' : ''}
              </span>
            )}
            {visionPending && (
              <span className="work-detail-vision-pending">
                <Sparkles size={12} aria-hidden="true" /> Analyse visuelle en cours…
              </span>
            )}
          </div>
        }
        actions={
          <div className="work-detail-actions">
            <PortfolioPublishButton work={work} />
            <Button
              variant="neutral"
              size="sm"
              onClick={handleGenerateDrafts}
              loading={isGenerating}
              disabled={!work.vision_extracted_at || isGenerating}
              title={
                work.vision_extracted_at
                  ? 'Générer les textes marketing pour Etsy / Pinterest / Instagram / TikTok'
                  : 'Attends que Claude Vision finisse l’analyse avant de générer les drafts'
              }
            >
              <Sparkles size={14} aria-hidden="true" /> Générer les drafts
            </Button>
            <KebabMenu ariaLabel={`Actions sur ${work.title_fr}`} size="md" items={kebabItems} />
          </div>
        }
      />

      {/* ── HERO 60/40 — image + cartel-de-musée ─────────────────────────── */}
      <section className="work-detail-hero" aria-label="Cartel de l'œuvre">
        <div className="work-detail-hero-image">
          {heroMedia ? (
            <MediaGallery
              items={[{ id: heroMedia.id, minioKey: heroMedia.minio_key }]}
              alt={work.title_fr}
              onSelect={(item) => setLightboxKey(item.minioKey)}
              className="work-detail-hero-gallery"
            />
          ) : (
            <div className="work-detail-hero-image-empty">
              <ImageIcon size={32} aria-hidden="true" />
              <span>Aucun média</span>
            </div>
          )}
        </div>

        <div className="work-detail-hero-cartel">
          <div className="work-detail-hero-cartel-top">
            <div className="work-detail-hero-status-row">
              <Badge
                variant={
                  work.status === 'published'
                    ? 'success'
                    : work.status === 'archived'
                      ? 'warning'
                      : 'default'
                }
                dot
              >
                {WORK_STATUS_FR[work.status ?? 'draft']}
              </Badge>
            </div>

            <p className="work-detail-hero-author">Stéphanie</p>

            <EditableTranslation
              lang={lang}
              valueFr={work.title_fr}
              valueEn={work.title_en}
              overridden={work.title_en_overridden}
              fieldLabel="Titre"
              onSubmit={handleSubmitTitleEn}
              as="h2"
              format="single"
              className="work-detail-hero-title"
            />

            {seriesSubtitle && <p className="work-detail-hero-subtitle">{seriesSubtitle}</p>}

            <div className="work-detail-hero-rule" aria-hidden="true" />

            <dl className="work-detail-hero-meta-grid">
              <div className="work-detail-hero-meta-cell">
                <dt>Technique</dt>
                <dd>
                  {work.technique_tags && work.technique_tags.length > 0
                    ? work.technique_tags.join(', ')
                    : '—'}
                </dd>
              </div>
              <div className="work-detail-hero-meta-cell">
                <dt>Ratio</dt>
                <dd>
                  <AspectRatioLabel value={work.aspect_ratio} />
                </dd>
              </div>
              <div className="work-detail-hero-meta-cell">
                <dt>Orientation</dt>
                <dd>{work.orientation ? WORK_ORIENTATION_FR[work.orientation] : '—'}</dd>
              </div>
              <div className="work-detail-hero-meta-cell">
                <dt>Date</dt>
                <dd>{work.effective_creation_date ?? '—'}</dd>
              </div>
            </dl>

            {(work.story_fr || work.story_en) && (
              <EditableTranslation
                lang={lang}
                valueFr={work.story_fr}
                valueEn={work.story_en}
                fieldLabel="Story"
                onSubmit={handleSubmitStoryEn}
                as="blockquote"
                format="multi"
                className="work-detail-hero-story"
              />
            )}
            {(work.description_fr || work.description_en) && (
              <EditableTranslation
                lang={lang}
                valueFr={work.description_fr}
                valueEn={work.description_en}
                fieldLabel="Description"
                onSubmit={handleSubmitDescriptionEn}
                as="p"
                format="multi"
                className="work-detail-hero-description"
              />
            )}
          </div>

          <div className="work-detail-hero-cartel-bottom">
            <span className="work-detail-hero-lang-label">Langue de fiche</span>
            <div
              className="work-detail-hero-lang-toggle"
              role="group"
              aria-label="Langue d'affichage"
            >
              <Button
                variant={lang === 'fr' ? 'neutral' : 'ghost'}
                size="sm"
                onClick={() => setLang('fr')}
                aria-pressed={lang === 'fr'}
                aria-label="Français"
              >
                <LangBadge lang="fr" />
              </Button>
              <Button
                variant={lang === 'en' ? 'neutral' : 'ghost'}
                size="sm"
                onClick={() => setLang('en')}
                aria-pressed={lang === 'en'}
                aria-label="English"
              >
                <LangBadge lang="en" />
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* ── Tabs ─────────────────────────────────────────────────────────── */}
      <Tabs<TabKey>
        value={tab}
        onChange={setTab}
        items={[
          { value: 'overview', label: 'Aperçu' },
          { value: 'content', label: 'Contenu généré' },
          { value: 'publications', label: 'Publications' },
        ]}
        ariaLabel="Sections de l'œuvre"
      />

      {tab === 'overview' && (
        <div className="work-detail-apercu">
          <div className="work-detail-apercu-main">
            {visionFailed && (
              <div className="ui-alert ui-alert--danger">
                <AlertTriangle size={16} aria-hidden="true" />
                <div>
                  <strong>Extraction Vision échouée.</strong> {visionJob?.last_error ?? ''}
                  <div className="work-detail-alert-action">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={handleRetryVision}
                      loading={retryVisionMutation.isPending}
                    >
                      Relancer
                    </Button>
                  </div>
                </div>
              </div>
            )}

            {/* ── 01 Analyse visuelle (Vision Tier 1) ────────────────────── */}
            {work.vision_extracted_at && (
              <FormSection
                number="01"
                title="Analyse visuelle"
                sub={
                  lang === 'fr'
                    ? translateQuery.isFetching
                      ? 'générée par Claude Vision · traduction en cours…'
                      : 'générée par Claude Vision · traduite à la volée'
                    : 'générée par Claude Vision · descripteurs EN'
                }
              >
                {work.visual_description && (
                  <p className="work-detail-vision-essay">
                    {tFr('visual_description', work.visual_description)}
                  </p>
                )}

                {palette.length > 0 && (
                  <div className="work-detail-vision-block">
                    <h4 className="work-detail-vision-eyebrow">Palette dominante</h4>
                    <ul className="work-detail-palette-list" role="list">
                      {palette.map((entry, i) => (
                        <li key={i} className="work-detail-palette-item">
                          <span
                            className="work-detail-swatch"
                            style={
                              entry.hex
                                ? ({ '--swatch-color': entry.hex } as React.CSSProperties)
                                : undefined
                            }
                            aria-hidden="true"
                          />
                          <span>{tFr(`palette:${i}`, entry.name_en ?? entry.hex ?? '')}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {(visionThemes.length > 0 || visionSubject) && (
                  <div className="work-detail-vision-block">
                    <h4 className="work-detail-vision-eyebrow">Genres détectés</h4>
                    <div className="work-detail-genre-row">
                      {visionThemes.map((t) => (
                        <Badge key={t.id} variant="default">
                          {tagLabel(t)}
                        </Badge>
                      ))}
                      {visionSubject && <Badge variant="info">{tagLabel(visionSubject)}</Badge>}
                    </div>
                  </div>
                )}

                {work.placement_suggestions && work.placement_suggestions.length > 0 && (
                  <div className="work-detail-vision-block">
                    <h4 className="work-detail-vision-eyebrow">Suggestions de placement</h4>
                    <div className="work-detail-genre-row">
                      {work.placement_suggestions.map((p, i) => (
                        <Badge key={p} variant="default">
                          {tFr(`placement:${i}`, p)}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}
              </FormSection>
            )}

            {/* ── Versions disponibles (multi-ratio, migration 015) ────── */}
            <WorkRatiosSection
              workId={id}
              media={allMedia}
              visionSourceMediaId={work.vision_source_media_id ?? null}
            />

            {/* ── Variantes (composant existant, header propre) ───────── */}
            <VariantsPanel
              workId={id}
              variants={work.variants ?? []}
              workSourceRatios={sourceRatios}
              formatMatchingTolerance={formatMatchingTolerance}
            />

            {/* ── Livrables digitaux (refonte POD v6.1 : routage par `medium_kind`) */}
            {(work.variants ?? [])
              .filter((v) => v.medium_kind === 'digital')
              .map((v) => (
                <DeliverablesPanel key={v.id} workId={id} variant={v} />
              ))}

            {/* ── Tags utilisateur (Vision exclus, déjà dans panel ci-dessus) */}
            {userTags.length > 0 && (
              <section className="work-detail-section">
                <h3 className="work-detail-section-title">Tags</h3>
                <div className="work-detail-genre-row">
                  {userTags.map((tag) => (
                    <Badge key={tag.id} variant="default">
                      {tag.label_fr}
                    </Badge>
                  ))}
                </div>
              </section>
            )}

            {/* ── Tier 2 — intention curatoriale ───────────────────────── */}
            {((work.emotional_intent?.length ?? 0) > 0 ||
              (work.target_audience?.length ?? 0) > 0 ||
              (work.influences?.length ?? 0) > 0) && (
              <section className="work-detail-section">
                <h3 className="work-detail-section-title">Intention</h3>
                <dl className="work-detail-metas">
                  {work.emotional_intent && work.emotional_intent.length > 0 && (
                    <>
                      <dt>Émotion</dt>
                      <dd className="work-detail-genre-row">
                        {work.emotional_intent.map((e) => (
                          <Badge key={e} variant="default">
                            {emotionalIntentLabel(e)}
                          </Badge>
                        ))}
                      </dd>
                    </>
                  )}
                  {work.target_audience && work.target_audience.length > 0 && (
                    <>
                      <dt>Public cible</dt>
                      <dd className="work-detail-genre-row">
                        {work.target_audience.map((t) => (
                          <Badge key={t} variant="default">
                            {targetAudienceLabel(t)}
                          </Badge>
                        ))}
                      </dd>
                    </>
                  )}
                  {work.influences && work.influences.length > 0 && (
                    <>
                      <dt>Influences</dt>
                      <dd className="work-detail-genre-row">
                        {work.influences.map((inf) => (
                          <Badge key={inf} variant="default">
                            {inf}
                          </Badge>
                        ))}
                      </dd>
                    </>
                  )}
                </dl>
              </section>
            )}

            {work.notes && (
              <section className="work-detail-section">
                <h3 className="work-detail-section-title">Atelier</h3>
                <p className="work-detail-notes">{work.notes}</p>
              </section>
            )}
          </div>

          {/* ── Sidebar — uniquement métadonnées techniques (carte bordée) */}
          <aside className="work-detail-apercu-side">
            <div className="work-detail-side-card">
              <header className="work-detail-side-card-header">Métadonnées techniques</header>
              <dl className="work-detail-side-rows">
                {work.orientation && (
                  <div className="work-detail-side-row">
                    <dt>Orientation</dt>
                    <dd>{WORK_ORIENTATION_FR[work.orientation]}</dd>
                  </div>
                )}
                {work.aspect_ratio !== null && work.aspect_ratio !== undefined && (
                  <div className="work-detail-side-row">
                    <dt>Ratio</dt>
                    <dd className="work-detail-side-row-num">
                      <AspectRatioLabel value={work.aspect_ratio} />
                    </dd>
                  </div>
                )}
                {work.effective_creation_date && (
                  <div className="work-detail-side-row">
                    <dt>Date de création</dt>
                    <dd className="work-detail-side-row-num">{work.effective_creation_date}</dd>
                  </div>
                )}
                {work.dominant_colors && work.dominant_colors.length > 0 && (
                  <div className="work-detail-side-row">
                    <dt>Couleurs dominantes</dt>
                    <dd className="work-detail-swatches">
                      {work.dominant_colors.map((hex, i) => (
                        <span
                          key={`${hex}-${i}`}
                          className="work-detail-swatch"
                          style={{ '--swatch-color': hex } as React.CSSProperties}
                          title={hex}
                          aria-label={hex}
                        />
                      ))}
                    </dd>
                  </div>
                )}
              </dl>
            </div>
          </aside>
        </div>
      )}

      {tab === 'content' && <DraftsTab workId={id} visionNotReady={visionPending} />}

      {tab === 'publications' && (
        <WorkPublicationsTab
          workId={id}
          workTitle={work.title_fr ?? work.title_en ?? `Œuvre #${id}`}
          variants={work.variants ?? []}
        />
      )}

      {/* ── Lightbox ─────────────────────────────────────────────────── */}
      <Modal
        isOpen={lightboxKey !== null}
        onClose={() => setLightboxKey(null)}
        ariaLabel={`Aperçu agrandi de ${work.title_fr}`}
        size="large"
        className="work-detail-lightbox"
      >
        {lightboxKey && (
          <div className="work-detail-lightbox-inner">
            <MediaImage minioKey={lightboxKey} alt={work.title_fr} />
          </div>
        )}
      </Modal>
    </Page>
  )
}
